/**
 * Graph Memory Manager for AskFelix
 *
 * This module manages memory usage by unloading heavy charts
 * when they are not visible or when there are too many heavy charts loaded.
 */

const graphMemoryManager = (function () {
  // Configuration
  const DEFAULT_CONFIG = {
    maxHeavyCharts: 3, // Maximum number of heavy charts to keep loaded simultaneously
    heavyChartThreshold: 5000, // Datapoints threshold to consider a chart "heavy"
    unloadDistance: 1000, // Pixel distance from viewport to trigger unload
    checkInterval: 1000, // Interval in ms to check chart visibility
    debugMode: false // Enable debug logs
  };

  // Internal state
  let config = { ...DEFAULT_CONFIG };
  let isEnabled = true;
  let isInitialized = false;
  let activeCharts = {};
  let checkIntervalId = null;

  // Helper functions
  const log = (message, force = false) => {
    if (config.debugMode || force) {
      console.log(`[GraphMemory] ${message}`);
    }
  };

  // Check if an element is in or near the viewport
  const isInViewport = element => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // Consider elements near the viewport (based on unloadDistance)
    const vertInView =
      rect.top <= windowHeight + config.unloadDistance && rect.bottom >= -config.unloadDistance;
    const horInView =
      rect.left <= windowWidth + config.unloadDistance && rect.right >= -config.unloadDistance;

    return vertInView && horInView;
  };

  // Count datapoints in a Plotly chart
  const countDatapoints = plotlyData => {
    if (!plotlyData || !Array.isArray(plotlyData)) return 0;

    let total = 0;
    plotlyData.forEach(trace => {
      // Handle different trace types
      if (Array.isArray(trace.x)) total += trace.x.length;
      if (Array.isArray(trace.y)) total += trace.y.length;
      if (Array.isArray(trace.z)) total += trace.z.length;
      // For heatmaps, contour plots, etc.
      if (Array.isArray(trace.z) && Array.isArray(trace.z[0])) {
        total += trace.z.length * trace.z[0].length;
      }
    });

    return total;
  };

  // Check if a chart is "heavy" (has many datapoints)
  const isHeavyChart = plotlyDiv => {
    // Check explicit data-heavy attribute
    if (plotlyDiv.hasAttribute('data-heavy')) {
      return plotlyDiv.getAttribute('data-heavy') === 'true';
    }

    // Check data-size attribute
    if (plotlyDiv.hasAttribute('data-size')) {
      const size = parseInt(plotlyDiv.getAttribute('data-size'), 10);
      return !isNaN(size) && size > config.heavyChartThreshold;
    }

    // Check actual data if loaded
    try {
      const plotlyObj = plotlyDiv._fullData || plotlyDiv.data;
      if (plotlyObj) {
        const points = countDatapoints(plotlyObj);
        return points > config.heavyChartThreshold;
      }
    } catch (e) {
      // Silent fail, we'll assume it's not heavy
    }

    return false;
  };

  // Unload a Plotly chart to free memory
  const unloadChart = (id, plotlyDiv, graphData) => {
    if (!plotlyDiv || !plotlyDiv._context) return false;

    try {
      // Get the query and dashboard information if available
      const query = plotlyDiv.dataset.query || '';
      const dashboardId = plotlyDiv.dataset.dashboardId || 'default';

      // Store current data for later reload
      activeCharts[id] = {
        isUnloaded: true,
        graphData: graphData || null,
        placeholder: true,
        query: query,
        dashboardId: dashboardId
      };

      // Replace with lightweight placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'chart-placeholder';
      placeholder.innerHTML = `
        <div class="chart-placeholder-message">
          <i class="material-icons">visibility_off</i>
          <span>Chart hidden to save memory</span>
          <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored reload-chart-btn">
            Show Chart
          </button>
        </div>
      `;

      // Store reference information needed for reload
      placeholder.dataset.chartId = id;
      placeholder.dataset.query = query;
      placeholder.dataset.dashboardId = dashboardId;

      // Replace the original div with the placeholder
      plotlyDiv.parentNode.replaceChild(placeholder, plotlyDiv);

      // Add click handler to reload chart
      placeholder.querySelector('.reload-chart-btn').addEventListener('click', e => {
        e.preventDefault();
        reloadChart(id, placeholder);
      });

      // Try to store the chart data in the cache if not already there
      if (window.graphCache && graphData) {
        window.graphCache
          .storeGraph(query, dashboardId, id, graphData)
          .then(success => {
            if (success) {
              log(`Successfully cached chart ${id} for later reload`);
            }
          })
          .catch(err => {
            console.error(`Error caching chart ${id}:`, err);
          });
      }

      // Dispose of Plotly to free memory
      Plotly.purge(plotlyDiv);

      log(`Unloaded heavy chart: ${id}`);
      return true;
    } catch (e) {
      console.error(`Error unloading chart ${id}:`, e);
      return false;
    }
  };

  // Reload a previously unloaded chart
  const reloadChart = (id, placeholder) => {
    try {
      if (!activeCharts[id]) {
        log(`No data found for chart ${id}`, true);
        return false;
      }

      log(`Reloading chart: ${id}`);

      // Before reloading, check if we need to unload other charts
      const heavyCharts = findHeavyChartsInViewport();
      if (heavyCharts.length >= config.maxHeavyCharts) {
        // Unload the oldest visible heavy chart that isn't this one
        const oldestChart = heavyCharts.filter(chartId => chartId !== id).pop();

        if (oldestChart) {
          const div = document.getElementById(`${oldestChart}graph-target`);
          if (div) unloadChart(oldestChart, div);
        }
      }

      // Show loading state
      placeholder.innerHTML = '<div class="chart-placeholder-message">Reloading chart...</div>';

      // Get the chart data from graphCache if available
      if (window.graphCache) {
        // Check if we have the chart data in cache
        const dashboardId = placeholder.dataset.dashboardId || 'default';
        const query = placeholder.dataset.query || '';

        window.graphCache
          .getGraph(query, dashboardId, id)
          .then(graphData => {
            if (graphData) {
              // Create the new chart element
              const chartDiv = document.createElement('div');
              chartDiv.id = `${id}graph-target`;
              chartDiv.classList.add('graph-target');

              // Replace placeholder with actual graph
              placeholder.parentNode.replaceChild(chartDiv, placeholder);

              // Render the chart using dataDisplay
              if (window.dataDisplay) {
                if (graphData.plotlyChartType === 'd3') {
                  // Handle D3 visualization
                  window.d3Renderer.render(chartDiv, graphData.d3Config);
                } else {
                  // Handle regular Plotly chart
                  window.dataDisplay.renderSinglePlotly(
                    id,
                    graphData.plotlyJSON,
                    chartDiv,
                    graphData.plotlyChartType
                  );
                }

                // Update active charts record
                activeCharts[id] = {
                  isUnloaded: false,
                  graphData: graphData,
                  placeholder: false
                };

                log(`Successfully reloaded chart ${id} from cache`);
                return true;
              }
            } else {
              log(`No cached data found for chart ${id}, falling back to reload`, true);
              // If no cached data is available, fallback to reloading the page
              window.location.reload();
            }
          })
          .catch(err => {
            console.error(`Error reloading chart ${id} from cache:`, err);
            window.location.reload();
          });
      } else {
        // Fallback if graphCache is not available
        log('GraphCache not available, falling back to reload', true);
        window.location.reload();
      }

      return true;
    } catch (e) {
      console.error(`Error reloading chart ${id}:`, e);
      // Fallback to full page reload
      window.location.reload();
      return false;
    }
  };

  // Find all heavy charts that are currently in the viewport
  const findHeavyChartsInViewport = () => {
    const heavyCharts = [];

    // Find all plotly graphs
    const graphDivs = document.querySelectorAll('[id$="graph-target"]');

    graphDivs.forEach(div => {
      if (!div.id) return;

      const id = div.id.replace('graph-target', '');

      if (isHeavyChart(div) && isInViewport(div)) {
        heavyCharts.push(id);
      }
    });

    return heavyCharts;
  };

  // Check and manage chart visibility/memory
  const checkChartVisibility = () => {
    if (!isEnabled) return;

    try {
      // Get all charts in viewport
      const heavyChartsInView = findHeavyChartsInViewport();

      if (heavyChartsInView.length > config.maxHeavyCharts) {
        log(`Too many heavy charts in viewport: ${heavyChartsInView.length}`);

        // Keep only the newest maxHeavyCharts charts
        const chartsToUnload = heavyChartsInView.slice(
          0,
          heavyChartsInView.length - config.maxHeavyCharts
        );

        chartsToUnload.forEach(id => {
          const div = document.getElementById(`${id}graph-target`);
          if (div) unloadChart(id, div);
        });
      }

      // Look for placeholders that are now in view and should be reloaded
      const placeholders = document.querySelectorAll('.chart-placeholder');
      placeholders.forEach(placeholder => {
        if (isInViewport(placeholder) && heavyChartsInView.length < config.maxHeavyCharts) {
          const id = placeholder.dataset.chartId;
          if (id) {
            reloadChart(id, placeholder);
          }
        }
      });
    } catch (e) {
      console.error('Error in chart visibility check:', e);
    }
  };

  // Public API
  return {
    // Initialize the memory manager
    init: function (customConfig = {}) {
      if (isInitialized) return;

      // Merge custom config with defaults
      config = { ...DEFAULT_CONFIG, ...customConfig };

      // Setup visibility checking interval
      checkIntervalId = setInterval(checkChartVisibility, config.checkInterval);

      // Setup scroll event handler for responsive checks
      window.addEventListener(
        'scroll',
        () => {
          // Debounce the scroll check
          if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(checkChartVisibility, 200);
        },
        { passive: true }
      );

      log('Memory manager initialized');
      isInitialized = true;
    },

    // Enable/disable memory management
    enable: function (enable = true) {
      isEnabled = enable;
      log(`Memory management ${enable ? 'enabled' : 'disabled'}`);

      if (enable && !checkIntervalId) {
        checkIntervalId = setInterval(checkChartVisibility, config.checkInterval);
      } else if (!enable && checkIntervalId) {
        clearInterval(checkIntervalId);
        checkIntervalId = null;
      }
    },

    // Manually unload a specific chart
    unloadChart: function (id) {
      const div = document.getElementById(`${id}graph-target`);
      if (div) {
        return unloadChart(id, div);
      }
      return false;
    },

    // Manually reload a specific chart
    reloadChart: function (id) {
      const placeholder = document.querySelector(`.chart-placeholder[data-chart-id="${id}"]`);
      if (placeholder) {
        return reloadChart(id, placeholder);
      }
      return false;
    },

    // Get current configuration
    getConfig: function () {
      return { ...config };
    },

    // Update configuration
    updateConfig: function (newConfig) {
      config = { ...config, ...newConfig };
      log('Configuration updated');

      // Update interval if it changed
      if (
        checkIntervalId &&
        newConfig.checkInterval &&
        newConfig.checkInterval !== DEFAULT_CONFIG.checkInterval
      ) {
        clearInterval(checkIntervalId);
        checkIntervalId = setInterval(checkChartVisibility, config.checkInterval);
      }

      return { ...config };
    },

    // Get stats about managed charts
    getStats: function () {
      const graphDivs = document.querySelectorAll('[id$="graph-target"]');
      const placeholders = document.querySelectorAll('.chart-placeholder');

      let totalHeavyCharts = 0;
      graphDivs.forEach(div => {
        if (isHeavyChart(div)) totalHeavyCharts++;
      });

      return {
        totalCharts: graphDivs.length,
        heavyCharts: totalHeavyCharts,
        unloadedCharts: placeholders.length,
        isEnabled: isEnabled,
        maxHeavyCharts: config.maxHeavyCharts
      };
    }
  };
})();

// Initialize if document is ready
document.addEventListener('DOMContentLoaded', function () {
  // Check if we're in a UI environment
  if (typeof Plotly !== 'undefined' && document.querySelector('.grid-stack')) {
    graphMemoryManager.init();
  }
});

if (typeof window !== 'undefined') {
  window.graphMemoryManager = graphMemoryManager;
}
